
export const validateVideoAndCompress = async (
  file,
  setCompressionProgress
) => {
  const response = {
    isValid: true,
    errorMessage: "",
    outputFile: file,
  };

  // First, validate file type
  const validVideoTypes = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime', // MOV files
    'video/x-msvideo', // AVI files
    'video/x-matroska' // MKV files
  ];

  if (!validVideoTypes.includes(file.type) && !file.type.startsWith('video/')) {
    response.isValid = false;
    response.errorMessage = "Please upload a valid video file";
    return response;
  }

  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("webkit-playsinline", "true");
  video.setAttribute("crossorigin", "anonymous");
  
  const objectUrl = URL.createObjectURL(file);
  video.src = objectUrl;

  try {
    const result = await new Promise((resolve) => {
      let metadataLoaded = false;
      
      video.onloadedmetadata = async () => {
        if (metadataLoaded) return; // Prevent double execution
        metadataLoaded = true;

        try {
          // Validate duration
          if (!isFinite(video.duration) || video.duration === 0) {
            response.isValid = false;
            response.errorMessage = "Unable to read video duration. The file may be corrupted.";
            URL.revokeObjectURL(objectUrl);
            resolve(response);
            return;
          }

          if (video.duration > 60) {
            response.isValid = false;
            response.errorMessage = "Video duration exceeds the maximum limit of 60 seconds";
            URL.revokeObjectURL(objectUrl);
            resolve(response);
            return;
          }

          // Check if video has valid dimensions
          if (!video.videoHeight || !video.videoWidth) {
            response.isValid = false;
            response.errorMessage = "Unable to read video dimensions. The file may be corrupted.";
            URL.revokeObjectURL(objectUrl);
            resolve(response);
            return;
          }

          // Only compress if resolution is higher than 480p
          const needsCompression = video.videoHeight > 480;

          if (needsCompression) {
            console.log("Starting video compression...");
            const compressionResponse = await compressVideo(
              video,
              480,
              setCompressionProgress
            );
            
            if (compressionResponse?.isValid) {
              compressionResponse.outputFile.fileName = file.name + "_compressed.webm";
              URL.revokeObjectURL(objectUrl);
              resolve(compressionResponse);
            } else {
              URL.revokeObjectURL(objectUrl);
              resolve(compressionResponse);
            }
          } else {
            // No compression needed - use original file
            URL.revokeObjectURL(objectUrl);
            resolve(response);
          }
        } catch (error) {
          console.error("Error in video processing:", error);
          response.isValid = false;
          response.errorMessage = "Error processing video file";
          URL.revokeObjectURL(objectUrl);
          resolve(response);
        }
      };

      video.onerror = (error) => {
        if (metadataLoaded) return;
        metadataLoaded = true;
        
        console.error("Video loading error:", error);
        response.isValid = false;
        
        // Provide more specific error messages based on common issues
        if (file.size > 100 * 1024 * 1024) { // 100MB
          response.errorMessage = "Video file is too large (max 100MB)";
        } else {
          response.errorMessage = "Unable to load video. The file format may not be supported or the file is corrupted.";
        }
        
        URL.revokeObjectURL(objectUrl);
        resolve(response);
      };

      // Timeout for video loading
      setTimeout(() => {
        if (!metadataLoaded) {
          metadataLoaded = true;
          console.error("Video loading timeout");
          response.isValid = false;
          response.errorMessage = "Video loading timeout. Please try a different file or check your internet connection.";
          URL.revokeObjectURL(objectUrl);
          resolve(response);
        }
      }, 15000); // 15 second timeout (increased for larger files)

      // Force load on some browsers
      video.load();
    });

    return result;
  } catch (error) {
    console.error("Error in validateVideoAndCompress:", error);
    URL.revokeObjectURL(objectUrl);
    response.isValid = false;
    response.errorMessage = "Error processing video file";
    return response;
  }
};

const compressVideo = async (
  videoElement,
  resolution,
  setCompressionProgress
) => {
  const response = {
    isValid: true,
    errorMessage: "",
    outputFile: null,
  };

  if (videoElement.videoHeight <= resolution) {
    response.isValid = true;
    return response;
  }

  try {
    const result = await new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: false });

      // Calculate new dimensions
      const aspectRatio = videoElement.videoWidth / videoElement.videoHeight;
      canvas.height = 480;
      canvas.width = Math.round(480 * aspectRatio);

      videoElement.muted = false;
      videoElement.playsInline = true;
      videoElement.setAttribute("webkit-playsinline", "true");
      videoElement.controls = false;

      // Audio handling with better error handling
      let audioContext;
      let audioSource;
      let audioDestination;
      
      try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioSource = audioContext.createMediaElementSource(videoElement);
        audioDestination = audioContext.createMediaStreamDestination();
        audioSource.connect(audioDestination);
      } catch (audioError) {
        console.warn("Audio context creation failed, continuing without audio:", audioError);
      }

      // Create video stream
      const videoStream = canvas.captureStream(30);
      
      // Combine streams
      const combinedTracks = [...videoStream.getVideoTracks()];
      if (audioDestination) {
        combinedTracks.push(...audioDestination.stream.getAudioTracks());
      }
      
      const combinedStream = new MediaStream(combinedTracks);

      // Check for supported MIME types
      const supportedTypes = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp8",
        "video/webm",
      ];
      
      let selectedMimeType = "";
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          selectedMimeType = type;
          break;
        }
      }

      const mediaRecorderOptions = selectedMimeType 
        ? { mimeType: selectedMimeType, videoBitsPerSecond: 800000 }
        : { videoBitsPerSecond: 800000 };

      const mediaRecorder = new MediaRecorder(combinedStream, mediaRecorderOptions);

      const chunks = [];
      const videoDuration = videoElement.duration;
      let startTime = Date.now();
      let recordingStopped = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          
          const elapsed = (Date.now() - startTime) / 1000;
          const progress = Math.min((elapsed / videoDuration) * 100, 99);
          setCompressionProgress(Math.round(progress));
        }
      };

      mediaRecorder.onstop = () => {
        if (recordingStopped) return;
        recordingStopped = true;
        
        setCompressionProgress(100);

        const compressedBlob = new Blob(chunks, { 
          type: mediaRecorder.mimeType || "video/webm" 
        });
        
        const compressedFile = new File(
          [compressedBlob],
          "compressed_video.webm",
          { type: compressedBlob.type }
        );

        const sizeAfterInMB = (compressedFile.size / 1048576).toFixed(2);
        if (sizeAfterInMB > 10) {
          response.isValid = false;
          response.errorMessage = `Compressed video size is ${sizeAfterInMB} MB, which exceeds the 10 MB limit.`;
        } else {
          response.outputFile = compressedFile;
        }

        // Clean up
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(console.warn);
        }

        setTimeout(() => {
          setCompressionProgress(0);
        }, 1000);

        resolve(response);
      };

      mediaRecorder.onerror = (event) => {
        if (recordingStopped) return;
        recordingStopped = true;
        
        console.error("MediaRecorder error:", event.error);
        response.isValid = false;
        response.errorMessage = "Error during video compression: " + (event.error?.message || "Unknown error");
        
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(console.warn);
        }
        
        resolve(response);
      };

      videoElement.onended = () => {
        if (mediaRecorder.state === "recording" && !recordingStopped) {
          mediaRecorder.stop();
        }
      };

      // Start recording
      try {
        mediaRecorder.start(100);
        videoElement.currentTime = 0;
        videoElement.loop = false;
        
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error("Video play failed:", error);
            if (!recordingStopped) {
              recordingStopped = true;
              response.isValid = false;
              response.errorMessage = "Failed to start video playback for compression";
              
              if (mediaRecorder.state === "recording") {
                mediaRecorder.stop();
              }
              
              resolve(response);
            }
          });
        }

        const drawFrame = () => {
          if (videoElement.ended || recordingStopped || mediaRecorder.state !== "recording") {
            return;
          }
          
          if (videoElement.readyState >= 2) {
            ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          }
          
          requestAnimationFrame(drawFrame);
        };

        drawFrame();
      } catch (startError) {
        console.error("Error starting compression:", startError);
        response.isValid = false;
        response.errorMessage = "Failed to start video compression";
        
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close().catch(console.warn);
        }
        
        resolve(response);
      }
    });

    return result;
  } catch (error) {
    console.error("Error in compressVideo:", error);
    response.isValid = false;
    response.errorMessage = "Error during video compression";
    return response;
  }
};

// SRV-20251219-000015
